import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Transactions from './pages/Transactions';
import Ingredients from './pages/Ingredients';
import Projects from './pages/Projects';
import Investments from './pages/Investments';
import InvestmentLedger from './pages/InvestmentLedger';
import CreditCards from './pages/CreditCards';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import ExceptionReview from './pages/ExceptionReview';
import FinalExpenseSheet from './pages/FinalExpenseSheet';
import Layout from './components/Layout';
import PWAInstallPrompt from './components/PWAInstallPrompt';

const API = '/api';

// Configure axios to always send the session token
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth Context
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback with hash, skip /auth/me.
    // AuthCallback will handle session exchange.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // Check if index.html pre-captured a session_id
    const pendingSessionId = localStorage.getItem('pending_session_id');
    if (pendingSessionId) {
      try {
        const response = await axios.post(`${API}/auth/session`, { session_id: pendingSessionId });
        if (response.data.session_token) {
          localStorage.setItem('session_token', response.data.session_token);
        }
        localStorage.removeItem('pending_session_id');
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem('pending_session_id');
        setUser(null);
      }
      setLoading(false);
      return;
    }

    // Normal auth check with stored token
    const storedToken = localStorage.getItem('session_token');
    if (!storedToken) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('session_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}); } catch {}
    localStorage.removeItem('session_token');
    localStorage.removeItem('pending_session_id');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// AuthCallback - exchanges session_id from URL hash for session token
const AuthCallback = () => {
  const { setUser, checkAuth } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      let sessionId = null;

      // Try to get from URL hash
      const hash = window.location.hash || '';
      const match = hash.match(/session_id=([^&]+)/);
      if (match && match[1]) {
        sessionId = match[1];
      }

      // Fallback: check localStorage (set by index.html pre-capture)
      if (!sessionId) {
        sessionId = localStorage.getItem('pending_session_id');
      }

      if (!sessionId) {
        // No session_id found anywhere - redirect to login
        navigate('/login', { replace: true });
        return;
      }

      try {
        const response = await axios.post(`${API}/auth/session`, { session_id: sessionId });
        if (response.data.session_token) {
          localStorage.setItem('session_token', response.data.session_token);
        }
        localStorage.removeItem('pending_session_id');
        // Clean the hash from URL
        window.history.replaceState(null, '', window.location.pathname);
        setUser(response.data);
        navigate('/dashboard', { replace: true });
      } catch (error) {
        localStorage.removeItem('pending_session_id');
        navigate('/login', { replace: true });
      }
    };

    processSession();
  }, [navigate, setUser, checkAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center">
        <div className="w-8 h-8 border-3 border-zinc-200 border-t-zinc-800 rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-sm text-zinc-500">Signing you in...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-8 h-8 border-3 border-zinc-200 border-t-zinc-800 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sm text-zinc-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Role-based route protection
const RoleRoute = ({ children, allowed }) => {
  const { user } = useAuth();
  if (!user || !allowed.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// App Router - CRITICAL: Check hash synchronously during render, NOT in useEffect
const AppRouter = () => {
  const location = useLocation();

  // Synchronous session_id detection - runs BEFORE any route or ProtectedRoute
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/dashboard" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
      <Route path="/transactions" element={<ProtectedRoute><Layout><Transactions /></Layout></ProtectedRoute>} />
      <Route path="/ingredients" element={<ProtectedRoute><Layout><Ingredients /></Layout></ProtectedRoute>} />
      <Route path="/projects" element={<ProtectedRoute><RoleRoute allowed={['owner','accountant']}><Layout><Projects /></Layout></RoleRoute></ProtectedRoute>} />
      <Route path="/investments" element={<ProtectedRoute><RoleRoute allowed={['owner','accountant']}><Layout><Investments /></Layout></RoleRoute></ProtectedRoute>} />
      <Route path="/investment-ledger" element={<ProtectedRoute><RoleRoute allowed={['owner','accountant']}><Layout><InvestmentLedger /></Layout></RoleRoute></ProtectedRoute>} />
      <Route path="/credit-cards" element={<ProtectedRoute><RoleRoute allowed={['owner','accountant']}><Layout><CreditCards /></Layout></RoleRoute></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><RoleRoute allowed={['owner','accountant']}><Layout><Reports /></Layout></RoleRoute></ProtectedRoute>} />
      <Route path="/exception-review" element={<ProtectedRoute><RoleRoute allowed={['owner','accountant']}><Layout><ExceptionReview /></Layout></RoleRoute></ProtectedRoute>} />
      <Route path="/expense-sheet" element={<ProtectedRoute><RoleRoute allowed={['owner','accountant']}><Layout><FinalExpenseSheet /></Layout></RoleRoute></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><RoleRoute allowed={['owner']}><Layout><Settings /></Layout></RoleRoute></ProtectedRoute>} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <PWAInstallPrompt />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
